import {
  AbsoluteFill,
  Sequence,
  OffthreadVideo,
  Audio,
  useCurrentFrame,
  interpolate,
} from "remotion";
import React from "react";
import { toHttpCdn } from "../utils";

// CDN 基础路径
const CDN_BASE = "https://cdn.xatlaz.com/remotion";

// 视频配置（包含游戏内时间戳）
const VIDEO_CONFIG = [
  { id: 0, file: `${CDN_BASE}/videos/2025_12_07_15_16_54_highlight_0_multikill_852-869.mp4`, start: 852, end: 869, duration: 22.833333 },
  { id: 1, file: `${CDN_BASE}/videos/2025_12_07_15_17_58_highlight_1_multikill_1113-1119.mp4`, start: 1113, end: 1119, duration: 11.833333 },
  { id: 2, file: `${CDN_BASE}/videos/2025_12_07_15_18_36_highlight_2_multikill_1114-1131.mp4`, start: 1114, end: 1131, duration: 22.833333 },
  { id: 3, file: `${CDN_BASE}/videos/2025_12_07_15_19_36_highlight_3_multikill_1498-1506.mp4`, start: 1498, end: 1506, duration: 13.833333 },
  { id: 4, file: `${CDN_BASE}/videos/2025_12_07_15_20_17_highlight_4_multikill_1640-1651.mp4`, start: 1640, end: 1651, duration: 16.833333 },
  { id: 5, file: `${CDN_BASE}/videos/2025_12_07_15_21_03_highlight_5_multikill_1661-1676.mp4`, start: 1661, end: 1676, duration: 20.833333 },
];

const AUDIO_URL = `${CDN_BASE}/audio/INVINCIBLE.mp3`;

const FPS = 30;
const TRANSITION_DURATION = 15;

// 画布尺寸
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1920;

// 视频原始比例 1280x800 = 16:10
// 放入 1080 宽度时，高度 = 1080 * (800/1280) = 675
const VIDEO_HEIGHT = 675;
const TOP_BANNER_HEIGHT = (CANVAS_HEIGHT - VIDEO_HEIGHT) / 2; // 约 622.5

// ===== 上方品牌区（DOTA2 标题） =====
const TopBanner: React.FC = () => {
  const frame = useCurrentFrame();

  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.5, 1, 0.5]);
  const particleOffset = (frame * 2) % 200;
  const floatY = interpolate(frame % 90, [0, 45, 90], [0, -5, 0]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT,
        background: "linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 动态背景线条 */}
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: `${(i * 25 + particleOffset) % 120 - 20}%`,
            width: 2,
            height: "100%",
            background: `linear-gradient(180deg, transparent, rgba(255,215,0,${0.1 + i * 0.05}), transparent)`,
            transform: "skewX(-20deg)",
          }}
        />
      ))}

      {/* DOTA2 标题 */}
      <div style={{ transform: `translateY(${floatY}px)`, textAlign: "center" }}>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 130,
            fontWeight: "bold",
            color: "#FF4444",
            textShadow: `
              0 0 40px rgba(255,68,68,${glowIntensity}),
              0 0 80px rgba(255,68,68,${glowIntensity * 0.5}),
              0 0 120px rgba(255,68,68,${glowIntensity * 0.3}),
              4px 4px 0 #000
            `,
            letterSpacing: 15,
          }}
        >
          DOTA2
        </div>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 60,
            color: "#FFD700",
            marginTop: 20,
            textShadow: `0 0 30px rgba(255,215,0,${glowIntensity})`,
            letterSpacing: 12,
          }}
        >
          超神集锦
        </div>
      </div>

      {/* 底部装饰线 */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,68,68,${glowIntensity}) 20%, 
            rgba(255,215,0,${glowIntensity}) 50%, 
            rgba(255,68,68,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />
    </div>
  );
};

// ===== 下方品牌区（@超神时刻） =====
const BottomBanner: React.FC = () => {
  const frame = useCurrentFrame();

  const breathe = interpolate(frame % 120, [0, 60, 120], [1, 1.05, 1]);
  const glowIntensity = interpolate(frame % 60, [0, 30, 60], [0.6, 1, 0.6]);

  // 浮动粒子
  const particles = [...Array(8)].map((_, i) => {
    const speed = 1 + (i % 3) * 0.5;
    const offset = (frame * speed + i * 45) % 360;
    const x = 50 + Math.sin((offset * Math.PI) / 180) * 40;
    const y = 50 + Math.cos((offset * Math.PI) / 180) * 25;
    const size = 3 + (i % 3) * 2;
    const opacity = 0.3 + (i % 4) * 0.15;
    return { x, y, size, opacity };
  });

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        height: TOP_BANNER_HEIGHT,
        background: "linear-gradient(0deg, #0a0a0a 0%, #1a1a2e 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* 顶部装饰线 */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,215,0,${glowIntensity}) 20%, 
            rgba(255,100,100,${glowIntensity}) 50%, 
            rgba(255,215,0,${glowIntensity}) 80%, 
            transparent 100%)`,
        }}
      />

      {/* 浮动粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: i % 2 === 0 ? "#FFD700" : "#FF6347",
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 2}px ${i % 2 === 0 ? "#FFD700" : "#FF6347"}`,
          }}
        />
      ))}

      {/* 主标题 */}
      <div style={{ transform: `scale(${breathe})`, textAlign: "center", zIndex: 10 }}>
        <span
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 90,
            fontWeight: "bold",
            background: "linear-gradient(90deg, #FFD700, #FFA500, #FF6347)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: `drop-shadow(0 0 20px rgba(255,215,0,${glowIntensity}))`,
            letterSpacing: 4,
          }}
        >
          @超神时刻
        </span>
        <div
          style={{
            fontFamily: "Arial Black, sans-serif",
            fontSize: 36,
            color: "rgba(255,255,255,0.7)",
            marginTop: 25,
            letterSpacing: 12,
          }}
        >
          GODLIKE MOMENTS
        </div>
      </div>
    </div>
  );
};

// ===== 全屏单视频组件（带品牌区） =====
const FullScreenVideo: React.FC<{ src: string }> = ({ src }) => {
  const frame = useCurrentFrame();


  const scale = interpolate(frame, [0, TRANSITION_DURATION], [1.02, 1], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 上方品牌区 */}
      <TopBanner />

      {/* 视频区域（居中） */}
      <div
        style={{
          position: "absolute",
          top: TOP_BANNER_HEIGHT,
          left: 0,
          width: "100%",
          height: VIDEO_HEIGHT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#000",
          overflow: "hidden",
        }}
      >
        <OffthreadVideo
          src={toHttpCdn(src)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transform: `scale(${scale})`,
          }}
        />
      </div>

      {/* 下方品牌区 */}
      <BottomBanner />
    </AbsoluteFill>
  );
};

// ===== 主组件 =====
export const HighlightVideo: React.FC = () => {
  // 视频帧数（跳过 v1，只用 v0, v2, v3, v4, v5）
  const v0Frames = Math.ceil(VIDEO_CONFIG[0].duration * FPS);
  const v2Frames = Math.ceil(VIDEO_CONFIG[2].duration * FPS);
  const v3Frames = Math.ceil(VIDEO_CONFIG[3].duration * FPS);
  const v4Frames = Math.ceil(VIDEO_CONFIG[4].duration * FPS);
  const v5Frames = Math.ceil(VIDEO_CONFIG[5].duration * FPS);

  // 计算各段起始帧（直接拼接，无转场）
  const START = {
    v0: 0,
    v2: v0Frames,
    v3: v0Frames + v2Frames,
    v4: v0Frames + v2Frames + v3Frames,
    v5: v0Frames + v2Frames + v3Frames + v4Frames,
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0a" }}>
      {/* 背景音乐：跳过前 10 秒进入高潮 */}
      <Audio src={toHttpCdn(AUDIO_URL)} startFrom={10 * FPS} volume={0.7} />

      {/* v0 全屏 */}
      <Sequence from={START.v0} durationInFrames={v0Frames}>
        <FullScreenVideo src={VIDEO_CONFIG[0].file} />
      </Sequence>

      {/* v2 全屏 */}
      <Sequence from={START.v2} durationInFrames={v2Frames}>
        <FullScreenVideo src={VIDEO_CONFIG[2].file} />
      </Sequence>

      {/* v3 全屏 */}
      <Sequence from={START.v3} durationInFrames={v3Frames}>
        <FullScreenVideo src={VIDEO_CONFIG[3].file} />
      </Sequence>

      {/* v4 全屏 */}
      <Sequence from={START.v4} durationInFrames={v4Frames}>
        <FullScreenVideo src={VIDEO_CONFIG[4].file} />
      </Sequence>

      {/* v5 全屏 */}
      <Sequence from={START.v5} durationInFrames={v5Frames}>
        <FullScreenVideo src={VIDEO_CONFIG[5].file} />
      </Sequence>
    </AbsoluteFill>
  );
};

// 导出视频配置供 Root 使用
export const HIGHLIGHT_VIDEO_CONFIG = {
  fps: FPS,
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  // 总时长: v0 + v2 + v3 + v4 + v5
  durationInFrames:
    Math.ceil(VIDEO_CONFIG[0].duration * FPS) +
    Math.ceil(VIDEO_CONFIG[2].duration * FPS) +
    Math.ceil(VIDEO_CONFIG[3].duration * FPS) +
    Math.ceil(VIDEO_CONFIG[4].duration * FPS) +
    Math.ceil(VIDEO_CONFIG[5].duration * FPS),
};
